'use client';

/**
 * The global waiting dialog — the one face the app wears whenever the honest
 * answer is "not yet".
 *
 * It is mounted ONCE, in the root layout, and nothing mounts it again. What
 * raises it is a hold taken through lib/wait.ts (read that file first: it says
 * why there is exactly one of these and what it promises); this file is only
 * what it looks like and how it comes and goes.
 *
 * ── What is on screen ───────────────────────────────────────────────────────
 * The KHARMA coin, tossing itself, in the centre of the window. It is the same
 * character and the same choreography as the typing hold (`Coin` in
 * TypingHold.tsx, `.gt-coin` in globals.css) on purpose: the product has one
 * way of saying "working on it", and somebody who has seen the coin flip while
 * they typed already knows what this means before they read a word.
 *
 * Around it, three things that are decoration, and one that is not:
 *
 *   halo     a slow ring of light turning behind the coin, and a breath of glow
 *   orbit    two rings of small sparks, turning opposite ways, never in step
 *   bar      a thin indeterminate track — motion that reads as progress without
 *            claiming a percentage nobody has
 *   LABEL    what is actually happening, in words: "Checking your sign-in",
 *            "Opening your notebook". This is the part that is not decoration.
 *            A spinner says "wait"; a sentence says what for, which is the
 *            difference between a pause and a worry.
 *
 * ── How it comes and goes (the tact) ────────────────────────────────────────
 * A wait layer that flashes is worse than none: a 90 ms flicker over a page
 * that was about to appear anyway is the app interrupting itself. So:
 *
 *   SHOW_AFTER   an ordinary hold must last this long before anything is drawn.
 *                Most fetches finish inside it and the layer never appears.
 *                (Boot and hard navigations skip it — there is nothing behind
 *                them to look at.)
 *   MIN_VISIBLE  once it IS up it stays at least this long, so it reads as a
 *                deliberate beat and the coin gets to leave the ground.
 *   FADE         it leaves by fading, never by cutting.
 *   SLOW_AFTER   past this it says so — "taking longer than usual" — because a
 *                wait that has stopped being normal should not pretend it is.
 *
 * It can never trap the page: holds expire on their own in lib/wait.ts, and if
 * the script never runs at all the server-rendered layer removes itself with a
 * CSS animation (`data-boot`, see `.gt-wait` in globals.css).
 *
 * ── Why it blocks clicks, and why it does not take focus ────────────────────
 * While it is up the page under it is either absent or about to be replaced,
 * so a click there lands on something that is not staying. Blocking the pointer
 * is the kind thing. Focus is left alone: the layer is a status, not a
 * question, and stealing the caret to announce "please wait" would be the same
 * mistake the typing hold refuses to make. It is `role="status"`, so a screen
 * reader hears the label once and is not interrupted.
 */

import { useEffect, useRef, useState } from 'react';
import { usePathname } from 'next/navigation';
import { endBootWait, endNavWait, useWaitState } from '@/lib/wait';
import { BRAND } from '@/lib/brand';
import { Coin } from './TypingHold';

const SHOW_AFTER = 260;
const MIN_VISIBLE = 700;
const FADE = 320;
const SLOW_AFTER = 8000;

/**
 * One line under the label, changing every few seconds.
 *
 * Something to read is what makes four seconds feel like two. They are facts
 * about the product rather than jokes about waiting: a tip is useful the tenth
 * time somebody sees it, and "hang tight!" is not.
 */
const TIPS = [
  'Every answer here is paid in KHARMA — and failed ones cost nothing.',
  'A1, A2 and B1 rewrite hard text as easier text, in the same language.',
  'Ctrl + Enter translates right away, without waiting for you to stop typing.',
  'Your daily KHARMA is waiting behind the gift in the header.',
  'Everything you translate is filed in your notebook automatically.',
  'Mistakes proofreads your writing in whatever language it is in.',
];

/** Sparks on the two orbits: angle around the ring, and size in px. */
const INNER = [{ a: 20, s: 9 }, { a: 140, s: 6 }, { a: 255, s: 8 }];
const OUTER = [{ a: 70, s: 6 }, { a: 165, s: 10 }, { a: 240, s: 5 }, { a: 330, s: 7 }];

const SPARK = 'M12 0l2.6 9.4L24 12l-9.4 2.6L12 24l-2.6-9.4L0 12l9.4-2.6z';

export default function GlobalWait() {
  const { holds } = useWaitState();
  const pathname = usePathname();

  const top = holds.length ? holds[holds.length - 1] : null;
  const busy = !!top;
  const instant = !!top?.instant;

  /*
   * `in` on the very first render, on the server and the client alike: the
   * store starts with the boot hold in place, so the first paint of the site
   * is this layer and not a white page.
   */
  const [phase, setPhase] = useState<'in' | 'out' | 'gone'>(busy ? 'in' : 'gone');
  /*
   * The label outlives the hold that set it. The layer fades for FADE ms after
   * the last hold is released, and a card whose sentence vanished at the start
   * of its own fade-out reads as a glitch.
   */
  const [label, setLabel] = useState(top?.label ?? '');
  const [hydrated, setHydrated] = useState(false);
  const [slow, setSlow] = useState(false);
  const [tip, setTip] = useState(0);
  const shownAt = useRef(0);

  // Hydrated: the page can now speak for itself, so the boot hold is returned.
  // Children's effects have already run by now (they precede this component in
  // the tree), so a page that needs the wait — the sign-in gate — is already
  // holding it and the layer never drops between the two.
  useEffect(() => {
    shownAt.current = Date.now();
    setHydrated(true);
    endBootWait();
  }, []);

  // The path changed, so the navigation that was holding the layer has landed.
  useEffect(() => { endNavWait(); }, [pathname]);

  useEffect(() => { if (top) setLabel(top.label); }, [top]);

  // The whole coming-and-going, as one small state machine. See the header.
  useEffect(() => {
    if (busy) {
      if (phase === 'in') return;
      // Caught mid-fade: come straight back rather than finishing the exit and
      // starting an entrance a moment later.
      const wait = phase === 'out' || instant ? 0 : SHOW_AFTER;
      const t = setTimeout(() => { shownAt.current = Date.now(); setSlow(false); setPhase('in'); }, wait);
      return () => clearTimeout(t);
    }
    if (phase === 'in') {
      const left = Math.max(0, MIN_VISIBLE - (Date.now() - shownAt.current));
      const t = setTimeout(() => setPhase('out'), left);
      return () => clearTimeout(t);
    }
    if (phase === 'out') {
      const t = setTimeout(() => setPhase('gone'), FADE);
      return () => clearTimeout(t);
    }
  }, [busy, instant, phase]);

  useEffect(() => {
    if (phase !== 'in') return;
    const late = setTimeout(() => setSlow(true), SLOW_AFTER);
    const turn = setInterval(() => setTip((n) => (n + 1) % TIPS.length), 3400);
    return () => { clearTimeout(late); clearInterval(turn); };
  }, [phase]);

  if (phase === 'gone') return null;

  return (
    <div
      className="gt-wait"
      data-phase={phase}
      /*
        Present only until hydration. It arms the CSS failsafe: if the script
        never runs, the layer dissolves by itself instead of covering a page
        that did load. Once React is here the timers above own the exit.
      */
      data-boot={hydrated ? undefined : ''}
      role="status"
      aria-live="polite"
      aria-busy={phase === 'in'}
    >
      <div className="gt-wait-card">
        <div className="gt-wait-stage" aria-hidden>
          <span className="gt-wait-glow" />
          <span className="gt-wait-halo" />
          <span className="gt-wait-orbit gt-wait-orbit-a">
            {INNER.map((p, i) => (
              <span key={i} className="gt-wait-arm" style={{ transform: `rotate(${p.a}deg)` }}>
                <svg viewBox="0 0 24 24" className="gt-spark" fill="currentColor"
                     style={{ width: p.s, height: p.s, animationDelay: `${i * 0.5}s` }}>
                  <path d={SPARK} />
                </svg>
              </span>
            ))}
          </span>
          <span className="gt-wait-orbit gt-wait-orbit-b">
            {OUTER.map((p, i) => (
              <span key={i} className="gt-wait-arm" style={{ transform: `rotate(${p.a}deg)` }}>
                <svg viewBox="0 0 24 24" className="gt-spark" fill="currentColor"
                     style={{ width: p.s, height: p.s, animationDelay: `${0.25 + i * 0.4}s` }}>
                  <path d={SPARK} />
                </svg>
              </span>
            ))}
          </span>
          <Coin className="gt-wait-coin" />
        </div>

        <p className="gt-wait-brand">{BRAND.name}</p>

        <p className="gt-wait-label">
          {label}
          <span className="gt-wait-dots" aria-hidden><i /><i /><i /></span>
        </p>

        <div className="gt-wait-bar" aria-hidden><span /></div>

        {/*
          `key` restarts the fade on every change, so a new line arrives rather
          than replacing the old one mid-word. Hidden from assistive tech: the
          label above is the announcement, and a tip read aloud every three
          seconds would be the layer talking over itself.
        */}
        <p key={slow ? 'slow' : tip} className="gt-wait-tip" aria-hidden={!slow}>
          {slow ? 'This is taking longer than usual — still working on it.' : TIPS[tip]}
        </p>
      </div>
    </div>
  );
}
